import { ReactNode } from 'react';
import { Dropdown } from 'semantic-ui-react';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { useSummaryDocument } from '~/components/SummaryDocumentButton';
import { useDealSpreadsheet } from '~/components/DealSpreadsheetButton';
import KeyDownloadRow from './KeyDownloadRow';

export function SummaryDocumentRow() {
  const { permissions } = usePermissions();
  const {
    hasUploadedDoc,
    downloadStatus,
    uploadStatus,
    removeFileStatus,
    replaceFileStatus,
    downloadFile,
    handleDeleteSummaryDocument,
    openFilePicker,
    getRootProps,
    getInputProps,
  } = useSummaryDocument();

  if (!permissions.isAdmin && !(permissions.isInvestor && hasUploadedDoc)) {
    return null;
  }

  const isAdmin = !!permissions.isAdmin;
  const isLoading = [downloadStatus, removeFileStatus, replaceFileStatus].includes('loading');

  const menuItems = isAdmin ? (
    <>
      <Dropdown.Item onClick={() => downloadFile()}>Download</Dropdown.Item>
      <Dropdown.Item onClick={() => openFilePicker()}>Replace</Dropdown.Item>
      <Dropdown.Item onClick={handleDeleteSummaryDocument}>Delete</Dropdown.Item>
    </>
  ) : (
    <Dropdown.Item onClick={() => downloadFile()}>Download</Dropdown.Item>
  );

  return (
    <KeyDownloadRow
      title="Deal summary"
      hasDocument={hasUploadedDoc}
      isAdmin={isAdmin}
      isLoading={isLoading}
      uploadStatus={uploadStatus}
      onClick={hasUploadedDoc && !isAdmin ? () => downloadFile() : undefined}
      getRootProps={getRootProps}
      getInputProps={getInputProps}
      menuItems={menuItems}
    />
  );
}

export function DealSpreadsheetRow() {
  const { permissions } = usePermissions();
  const {
    hasUploadedDoc,
    downloadStatus,
    uploadStatus,
    removeFileStatus,
    replaceFileStatus,
    downloadFile,
    handleDeleteUploadedSpreadsheet,
    handleGenerateSpreadsheet,
    handleGenerateLegacySpreadsheet,
    openFilePicker,
    getRootProps,
    getInputProps,
  } = useDealSpreadsheet();

  const isAdmin = !!permissions.isAdmin;
  const isInvestor = !!permissions.isInvestor;
  const isLoading = [downloadStatus, removeFileStatus, replaceFileStatus].includes('loading');

  let menuItems: ReactNode;
  let onClick: (() => void) | undefined;

  if (isAdmin) {
    menuItems = (
      <>
        {hasUploadedDoc ? (
          <>
            <Dropdown.Item onClick={() => downloadFile()}>Download</Dropdown.Item>
            <Dropdown.Item onClick={() => openFilePicker()}>Replace</Dropdown.Item>
            <Dropdown.Item onClick={handleDeleteUploadedSpreadsheet}>Delete</Dropdown.Item>
          </>
        ) : (
          <Dropdown.Item onClick={() => openFilePicker()}>Upload new version</Dropdown.Item>
        )}
        <Dropdown.Item onClick={handleGenerateSpreadsheet}>Download generated version</Dropdown.Item>
        <Dropdown.Item onClick={handleGenerateLegacySpreadsheet}>Download legacy generated version</Dropdown.Item>
      </>
    );
  } else if (isInvestor && hasUploadedDoc) {
    menuItems = <Dropdown.Item onClick={() => downloadFile()}>Download</Dropdown.Item>;
    onClick = () => downloadFile();
  } else if (isInvestor) {
    menuItems = <Dropdown.Item onClick={handleGenerateLegacySpreadsheet}>Download</Dropdown.Item>;
    onClick = handleGenerateLegacySpreadsheet;
  } else {
    return null;
  }

  // For admin we always show a row (with or without uploaded doc) because there are always actions.
  // The KeyDownloadRow treats "no document for admin" as an upload affordance, but here we want the
  // dropdown to be the primary affordance instead. So always pass hasDocument=true for admin.
  return (
    <KeyDownloadRow
      title="Deal spreadsheet"
      hasDocument={isAdmin ? true : hasUploadedDoc}
      isAdmin={isAdmin}
      isLoading={isLoading}
      uploadStatus={uploadStatus}
      onClick={onClick}
      getRootProps={getRootProps}
      getInputProps={getInputProps}
      menuItems={menuItems}
    />
  );
}
